<template>
    <div v-if="isInitialising" class="brand-loader-wrapper">
        <brand-loader />
    </div>
    <div v-else class="document-viewer" :class="{ 'modal-mode': isModalMode, 'preview-mode': props.previewMode, 'linkedin-embed-mode': showLinkedInEmbed }">
        <div v-if="!isModalMode && !props.noBreadcrumb" class="breadcrumbs-holder">
            <router-link
                :to="backLink"
                class="mobile-link"
            >
                <font-awesome-icon class="arrowLeft" :icon="['fas', 'arrow-left']" /> Back
            </router-link>
            <breadcrumb-with-underlining :breadcrumbItems="breadcrumbItems" class="breadcrumbs" />
        </div>
        <div class="main-content">
            <div
                class="spark-content-card"
                :class="{
                    'card-main': !isModalMode && !props.previewMode,
                    'full-height': showLinkedInEmbed,
                }"
            >
                <report-details
                    class="spark-content-report-details"
                    :allowZoom="false"
                    :allow-bookmark="!props.previewMode"
                    :hasAccess="props.sparkContentDetails.hasAccess"
                    :description="props.sparkContentDetails.description"
                    :publication-date="props.sparkContentDetails.publicationDate"
                    :authors="props.sparkContentDetails.authors"
                    :filters="props.sparkContentDetails.filters"
                    :title="props.sparkContentDetails.documentTitle"
                    :subtitle="displaySubtitle"
                    :access-reason="props.sparkContentDetails.accessReason"
                    :report-id="props.sparkContentDetails.documentShortCode"
                    :report-type="props.sparkContentDetails.reportType"
                    :header-background-image-url="headerBackgroundImageUrl"
                    :alternative-tags="richContentHeaderTags"
                    :edit-mode="reportDetailsEditMode"
                    pad-header-without-background
                    @edit="openRichContentEditor"
                    @header-tag-click="handleHeaderTagClick"
                >
                    <template #header-controls>
                        <primevue-button
                            v-if="showLinkedInLink"
                            text
                            class="plain-btn ctrl-buttons linkedin-link desktop-linkedin-link"
                            @click="openExternalLink(linkedInUrl)"
                        >
                            <font-awesome-icon class="linkedin-icon" :icon="['fab', 'linkedin']" />
                            View on LinkedIn
                            <font-awesome-icon :icon="['fas', 'arrow-up-right-from-square']" />
                        </primevue-button>
                    </template>
                    <template #after-tags>
                        <primevue-button
                            v-if="showLinkedInLink"
                            text
                            class="plain-btn linkedin-link mobile-linkedin-link"
                            @click="openExternalLink(linkedInUrl)"
                        >
                            <font-awesome-icon class="linkedin-icon" :icon="['fab', 'linkedin']" />
                            View on LinkedIn
                            <font-awesome-icon :icon="['fas', 'arrow-up-right-from-square']" />
                        </primevue-button>
                    </template>
                </report-details>
                <div
                    class="report-section"
                    :class="{
                        'no-access': !props.sparkContentDetails.hasAccess,
                        'full-height': showLinkedInEmbed,
                    }"
                >
                    <div v-if="props.sparkContentDetails.hasAccess" class="spark-content-body">
                        <spark-content-podcast-block
                            v-if="isPodcast && spotifyEmbedUrl"
                            :embed-url="spotifyEmbedUrl"
                            :embed-title="spotifyEmbedTitle"
                            :embed-min-height="spotifyEmbedMinHeight"
                        />
                        <spark-content-webinar-card
                            v-if="isWebinar"
                            :is-upcoming="isPublicationDateInFuture(props.sparkContentDetails.publicationDate)"
                            @open="openExternalLink(externalUrl)"
                        />
                        <div v-if="showLinkedInEmbed" class="linkedin-embed-wrapper">
                            <iframe
                                :src="linkedInEmbedUrl!"
                                frameborder="0"
                                allowfullscreen
                                title="Embedded LinkedIn post"
                            />
                        </div>
                        <spark-content-body-html
                            v-if="shouldRenderBodyHtml"
                            :html="props.sparkContentDetails.bodyHtml"
                        />
                    </div>
                    <div class="no-access-window" v-else>
                        <no-report-access />
                    </div>
                    <document-comments
                        v-if="isModalMode && !props.previewMode"
                        class="spark-content-modal-comments"
                        :document-short-code="props.sparkContentDetails.documentShortCode"
                        :initial-comment-count="props.sparkContentDetails.hasAccess ? displayedCommentCount : 0"
                        :locked="!props.sparkContentDetails.hasAccess"
                        :allow-expand="props.allowCommentsExpand"
                        @comment-count-updated="updateDisplayedCommentCount"
                    />
                </div>
            </div>
        </div>
        <div v-if="!props.previewMode" class="side-content">
            <div v-if="canShareSparkContent" class="transpose-options">
                <primevue-button text class="plain-btn extra-padding grow" @click="showShareModal = true">
                    Share
                    <font-awesome-icon :icon="['fas', 'share-nodes']" />
                </primevue-button>
            </div>
            <div v-if="!isModalMode && !props.previewMode" class="options">
                <document-comments
                    :document-short-code="props.sparkContentDetails.documentShortCode"
                    :initial-comment-count="props.sparkContentDetails.hasAccess ? displayedCommentCount : 0"
                    :locked="!props.sparkContentDetails.hasAccess"
                    compact
                    :allow-expand="props.allowCommentsExpand"
                    @comment-count-updated="updateDisplayedCommentCount"
                />
            </div>
            <div class="options" v-if="!isThreadContent">
                <div class="title">{{ sidePanelTitle }}</div>
                <div class="description">{{ sidePanelDescription }}</div>
                <div class="action-buttons">
                    <primevue-button v-if="showOpenExternalButton" class="plain-btn" @click="openExternalLink(externalUrl)">
                        {{ externalButtonLabel }}
                        <font-awesome-icon :icon="['fas', 'arrow-up-right-from-square']" />
                    </primevue-button>
                </div>
                <our-experts-section :our-experts="ourExperts" />
                <div>
                    <primevue-button
                        text
                        @click="openContactUs"
                        class="plain-btn"
                    >
                        Speak to our experts
                        <font-awesome-icon :icon="['fas', 'user']" />
                    </primevue-button>
                </div>
            </div>
        </div>
    </div>
    <contact-us
        v-if="!props.previewMode"
        v-model:visible="showContactUsModal"
        @close="showContactUsModal = false"
        :documentTitle="props.sparkContentDetails.documentTitle"
        :our-experts="ourExperts"
    />
    <central-modal
        v-model:visible="showRichContentEditor"
        header="Edit community thread"
        :size="{ width: '100rem', height: '95vh' }"
    >
        <div v-if="isLoadingRichContentForEdit" class="edit-loader">
            <component-loader />
        </div>
        <community-thread-editor
            v-else-if="richContentEditorThread"
            :key="`rich-content-editor-${props.sparkContentDetails.documentShortCode}`"
            :thread="richContentEditorThread"
            :is-saving="isSavingRichContent"
            :can-delete="false"
            @save="saveRichContent"
        />
    </central-modal>
    <central-modal
        v-model:visible="showShareModal"
        header="Share"
        :size="{ width: '70rem', height: '32rem' }"
    >
        <share-document-url :title="props.sparkContentDetails.documentTitle" />
    </central-modal>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import PrimevueButton from 'primevue/button';
import { useHead, useSeoMeta } from '@unhead/vue';
import { IBreadcrumbItem } from '@/interfaces/general/IBreadcrumbItem.ts';
import { createDocumentBreadcrumbItems } from '@/utils/documentBreadcrumbUtils.ts';
import { router } from '@/router.ts';
import { IExpert } from '@/interfaces/deltaGpt/IExpert.ts';
import { IAdminSparkContent, ISparkContentDetails } from '@/interfaces/deltaGpt/IDocument.ts';
import { deltaGptApi } from '@/api/deltaGpt/deltaGptApi.ts';
import { deltaGptRoutes } from '@/routes/deltaGptRoutes.ts';
import { getResearchSectionRouteName } from '@/routes/routeUtils.ts';
import { getNames } from '@/utils/documentFiltersUtils.ts';
import { SPARK_DEFAULT_OG_IMAGE_URL, SPARK_SITE_URL } from '@/constants/SparkSite.ts';
import { stripHtml } from '@/utils/htmlUtils.ts';
import { getDisplayCommunityThreadPageLinks } from '@/utils/communityPageLinkRegistry.ts';
import BreadcrumbWithUnderlining from '@/components/common/BreadcrumbWithUnderlining.vue';
import ReportDetails from '@/components/deltaGpt/ReportDetails.vue';
import DocumentComments from '@/components/deltaGpt/DocumentComments.vue';
import NoReportAccess from '@/components/deltaGpt/NoReportAccess.vue';
import OurExpertsSection from '@/components/deltaGpt/OurExperts.vue';
import ContactUs from '@/components/deltaGpt/ContactUs.vue';
import SparkContentPodcastBlock from '@/components/deltaGpt/sparkContent/SparkContentPodcastBlock.vue';
import SparkContentWebinarCard from '@/components/deltaGpt/sparkContent/SparkContentWebinarCard.vue';
import SparkContentBodyHtml from '@/components/deltaGpt/sparkContent/SparkContentBodyHtml.vue';
import BrandLoader from '@/components/layout/BrandLoader.vue';
import ComponentLoader from '@/components/layout/ComponentLoader.vue';
import CentralModal from '@/components/common/CentralModal.vue';
import CommunityThreadEditor from '@/components/community/CommunityThreadEditor.vue';
import ShareDocumentUrl from '@/components/deltaGpt/ShareDocumentUrl.vue';
import { ICommunityThreadDetails, ICommunityThreadSaveRequest } from '@/interfaces/community/ICommunityThread.ts';
import { getReportTypeLabel, isLinkedInReportType, isThreadReportType } from '@/utils/researchContent.ts';
import {
    getSiteAreaRelevanceLabel,
    getSubscriptionFilterIdForSiteArea,
} from '@/utils/siteAreaRelevanceUtils.ts';
import { useDeltaGptStore } from '@/stores/deltaGpt/deltaGptStore.ts';
import { useToast } from 'primevue/usetoast';
import { isPublicationDateInFuture } from '@/utils/sparkContentUtils.ts';

const TALKING_NEW_ENERGY_SPOTIFY_SHOW_URL = 'https://open.spotify.com/show/2NRYwxIWfCgNikmvuSKrVQ';

const props = withDefaults(defineProps<{
    sparkContentDetails: ISparkContentDetails;
    displayMode?: 'page' | 'modal';
    noBreadcrumb?: boolean;
    previewMode?: boolean;
    allowCommentsExpand?: boolean;
}>(), {
    displayMode: 'page',
    noBreadcrumb: false,
    previewMode: false,
    allowCommentsExpand: true,
});

const route = useRoute();
const emit = defineEmits<{
    updated: [void];
    navigated: [void];
}>();

const deltaGptStore = useDeltaGptStore();
const toast = useToast();
const ourExperts = ref<Array<IExpert>>([]);
const showContactUsModal = ref<boolean>(false);
const showRichContentEditor = ref<boolean>(false);
const showShareModal = ref<boolean>(false);
const isLoadingRichContentForEdit = ref<boolean>(false);
const isSavingRichContent = ref<boolean>(false);
const richContentEditorThread = ref<ICommunityThreadDetails | null>(null);
const richContentEditorDocument = ref<IAdminSparkContent | null>(null);
const isInitialising = ref<boolean>(false);
const displayedCommentCount = ref<number>(props.sparkContentDetails.commentCount);

const isModalMode = computed<boolean>(() => props.displayMode === 'modal');
const isPodcast = computed<boolean>(() => props.sparkContentDetails.reportType === 'Podcast');
const isWebinar = computed<boolean>(() => props.sparkContentDetails.reportType === 'Webinar');
const isLinkedInPost = computed<boolean>(() => isLinkedInReportType(props.sparkContentDetails.reportType));
const isThreadContent = computed<boolean>(() => isThreadReportType(props.sparkContentDetails.reportType));
const canShareSparkContent = computed<boolean>(() => props.sparkContentDetails.hasAccess && !props.previewMode && !isModalMode.value);
const isDefinition = computed<boolean>(() => props.sparkContentDetails.reportType === 'Definition');
const linkedInUrl = computed<string>(() => props.sparkContentDetails.linkedinUrl?.trim() ?? '');
const linkedInEmbedUrl = computed<string>(() => props.sparkContentDetails.linkedinEmbedUrl?.trim() ?? '');
const showLinkedInLink = computed<boolean>(() => isThreadContent.value && linkedInUrl.value.length > 0);
const showLinkedInEmbed = computed<boolean>(() => isLinkedInPost.value && linkedInEmbedUrl.value.length > 0);
const reportDetailsEditMode = computed<'admin' | 'local' | 'hidden'>(() => {
    if (props.previewMode) return 'hidden';
    if (isThreadContent.value) return 'local';
    return 'admin';
});
const headerBackgroundImageUrl = computed<string | null>(() => props.sparkContentDetails.fullImageUrl || null);
const shouldRenderBodyHtml = computed<boolean>(() => !isPodcast.value && !isWebinar.value && !isLinkedInPost.value && !!props.sparkContentDetails.bodyHtml?.trim());
const researchListRouteName = computed<string>(() => String(getResearchSectionRouteName(route, deltaGptRoutes.documentList.name)));
const backLink = computed(() => (
    isThreadContent.value
        ? { name: researchListRouteName.value, query: route.query }
        : { name: getResearchSectionRouteName(route, deltaGptRoutes.documentList.name), query: route.query }
));

const breadcrumbItems = computed<Array<IBreadcrumbItem>>(() => createDocumentBreadcrumbItems(
    props.sparkContentDetails.documentTitle,
    props.sparkContentDetails.documentShortCode,
    (): void => {
        router.push(backLink.value);
    },
));

function normalizeSummaryText (value?: string | null): string {
    if (!value) return '';

    return stripHtml(value)
        .replace(/\s+/g, ' ')
        .trim()
        .toLocaleLowerCase();
}

const displaySubtitle = computed<string | null>(() => {
    if (isThreadContent.value) return null;

    const subtitle = normalizeSummaryText(props.sparkContentDetails.documentSubtitle);
    const description = normalizeSummaryText(props.sparkContentDetails.description);

    if (subtitle.length > 0 && subtitle === description) {
        return null;
    }

    return props.sparkContentDetails.documentSubtitle ?? null;
});

const spotifyEpisodeId = computed<string | null>(() => {
    const spotifyUrl = props.sparkContentDetails.spotifyUrl;
    if (!spotifyUrl) return null;

    const urlMatch = spotifyUrl.match(/spotify\.com\/episode\/([^?]+)/i);
    const uriMatch = spotifyUrl.match(/spotify:episode:([^?]+)/i);
    return urlMatch?.[1] || uriMatch?.[1] || null;
});

const spotifyEmbedUrl = computed<string | null>(() => {
    if (!isPodcast.value) return null;
    if (spotifyEpisodeId.value) {
        return `https://open.spotify.com/embed/episode/${spotifyEpisodeId.value}?utm_source=generator`;
    }

    return `${TALKING_NEW_ENERGY_SPOTIFY_SHOW_URL.replace('/show/', '/embed/show/')}?utm_source=generator`;
});

const spotifyEmbedMinHeight = computed<string>(() => (spotifyEpisodeId.value ? '352px' : '232px'));
const spotifyEmbedTitle = computed<string>(() => (spotifyEpisodeId.value ? 'Spotify podcast episode' : 'Spotify podcast show'));
const spotifyOpenUrl = computed<string>(() => (
    spotifyEpisodeId.value
        ? `https://open.spotify.com/episode/${spotifyEpisodeId.value}`
        : props.sparkContentDetails.spotifyUrl ?? TALKING_NEW_ENERGY_SPOTIFY_SHOW_URL
));

const displayPageLinks = computed(() => getDisplayCommunityThreadPageLinks(
    (props.sparkContentDetails.routeNames ?? []).map(routeName => ({ routeName })),
));
const richContentHeaderTags = computed(() => {
    if (!isThreadContent.value) return [];

    return [
        ...(isThreadReportType(props.sparkContentDetails.reportType) ? [{
            id: `report-type-${props.sparkContentDetails.reportType}`,
            name: getReportTypeLabel(props.sparkContentDetails.reportType),
            severity: 'secondary' as const,
            clickable: true,
            reportType: props.sparkContentDetails.reportType,
        }] : []),
        ...displayPageLinks.value.map(pageLink => ({
            id: `page-${pageLink.key}`,
            name: pageLink.label,
            severity: 'info' as const,
            clickable: true,
            routeName: pageLink.routeName,
        })),
        ...props.sparkContentDetails.siteAreaRelevance
            .filter(siteArea => !displayPageLinks.value.some(pageLink => pageLink.label === getSiteAreaRelevanceLabel(siteArea)))
            .map(siteArea => {
                const subscriptionFilterId = getSubscriptionFilterIdForSiteArea(siteArea);
                return {
                    id: `relevance-${siteArea}`,
                    name: getSiteAreaRelevanceLabel(siteArea),
                    severity: 'success' as const,
                    clickable: true,
                    filterId: subscriptionFilterId ?? undefined,
                    routeName: subscriptionFilterId ? undefined : researchListRouteName.value,
                };
            }),
    ];
});

const externalUrl = computed<string | null>(() => {
    if (isPodcast.value) return spotifyOpenUrl.value;
    return props.sparkContentDetails.externalUrl ?? null;
});

const showOpenExternalButton = computed<boolean>(() => !!externalUrl.value);
const externalButtonLabel = computed<string>(() => {
    if (isPodcast.value) return 'Open in Spotify';
    if (isWebinar.value) {
        return isPublicationDateInFuture(props.sparkContentDetails.publicationDate) ? 'Sign up' : 'Open webinar';
    }
    return 'Open link';
});

const sidePanelTitle = computed<string>(() => {
    if (isPodcast.value) return 'Podcast';
    if (isWebinar.value) return 'Webinar';
    if (isDefinition.value) return 'Definition';
    return 'Research content';
});

const sidePanelDescription = computed<string>(() => {
    if (isPodcast.value) return 'Listen on Spotify and follow Talking New Energy across the major podcast platforms.';
    if (isWebinar.value) {
        return isPublicationDateInFuture(props.sparkContentDetails.publicationDate)
            ? 'Sign up for the webinar in a new tab.' : 'Open the webinar landing page in a new tab.';
    }
    if (isThreadContent.value) return 'Discussion and update content from the Spark team.';
    if (isDefinition.value) return 'A glossary definition stored directly in Spark.';
    return 'Shared research content.';
});

const canonicalUrl = computed<string>(() => {
    if (isDefinition.value) return `${SPARK_SITE_URL}/power/enact/markets/glossary/spark_${props.sparkContentDetails.documentShortCode}`;
    return `${SPARK_SITE_URL}/research/document/${props.sparkContentDetails.documentShortCode}`;
});

const schemaObject = computed<Record<string, unknown>>(() => {
    if (isDefinition.value) {
        return {
            '@context': 'https://schema.org',
            '@type': 'DefinedTerm',
            name: props.sparkContentDetails.documentTitle,
            description: props.sparkContentDetails.description,
            termCode: props.sparkContentDetails.documentShortCode,
        };
    }

    return {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: props.sparkContentDetails.documentTitle,
        description: props.sparkContentDetails.description,
        datePublished: props.sparkContentDetails.publicationDate,
        dateModified: props.sparkContentDetails.publicationDate,
        author: props.sparkContentDetails.authors,
        keywords: getNames(props.sparkContentDetails.filters),
        image: props.sparkContentDetails.thumbnailUrl || SPARK_DEFAULT_OG_IMAGE_URL,
    };
});

useHead(() => ({
    link: [
        { rel: 'canonical', href: canonicalUrl.value },
    ],
    script: [
        {
            key: `spark-content-schema-${props.sparkContentDetails.documentShortCode}`,
            type: 'application/ld+json',
            textContent: JSON.stringify(schemaObject.value),
        },
    ],
}));

useSeoMeta({
    title: computed(() => props.sparkContentDetails.documentTitle),
    ogTitle: computed(() => props.sparkContentDetails.documentTitle),
    twitterTitle: computed(() => props.sparkContentDetails.documentTitle),

    description: computed(() => props.sparkContentDetails.description),
    ogDescription: computed(() => props.sparkContentDetails.description),
    twitterDescription: computed(() => props.sparkContentDetails.description),

    ogUrl: canonicalUrl,
    twitterCard: 'summary_large_image',
    articlePublishedTime: props.sparkContentDetails.publicationDate,
    articleModifiedTime: props.sparkContentDetails.publicationDate,
    articleTag: computed(() => getNames(props.sparkContentDetails.filters)),
});

function openContactUs (): void {
    showContactUsModal.value = true;
}

function updateDisplayedCommentCount (commentCount: number): void {
    displayedCommentCount.value = commentCount;
}

function openExternalLink (link: string | null): void {
    if (!link) return;
    window.open(link, '_blank', 'noopener,noreferrer');
}

async function handleHeaderTagClick (headerTag: { routeName?: string; reportType?: string }): Promise<void> {
    if (headerTag.routeName) {
        await router.push({ name: headerTag.routeName });
        emit('navigated');
        return;
    }

    if (headerTag.reportType) {
        await router.push({
            name: researchListRouteName.value,
            query: { reportType: headerTag.reportType },
        });
        emit('navigated');
    }
}

async function openRichContentEditor (): Promise<void> {
    if (!isThreadContent.value) return;

    showRichContentEditor.value = true;
    isLoadingRichContentForEdit.value = true;

    try {
        const adminDocuments = await deltaGptApi.getAllAdminSparkContentInfo();
        const adminDocument = adminDocuments.find(document => document.documentShortCode === props.sparkContentDetails.documentShortCode);
        if (!adminDocument) throw new Error('Could not find rich content admin details.');

        richContentEditorDocument.value = adminDocument;
        richContentEditorThread.value = mapRichContentToEditorThread(adminDocument);
    } catch {
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Could not load this Community thread for editing.',
            life: 5000,
        });
        showRichContentEditor.value = false;
    } finally {
        isLoadingRichContentForEdit.value = false;
    }
}

async function saveRichContent (payload: ICommunityThreadSaveRequest): Promise<void> {
    if (!richContentEditorDocument.value) return;

    isSavingRichContent.value = true;

    try {
        const updatedDocument: IAdminSparkContent = {
            ...richContentEditorDocument.value,
            publicationDate: payload.publicationDate,
            documentTitle: payload.title,
            authors: payload.authors,
            description: payload.description,
            bodyHtml: payload.bodyHtml,
            linkedinUrl: payload.linkedinUrl?.trim() ?? '',
            linkedinEmbedUrl: payload.linkedinEmbedUrl?.trim() ?? '',
            isDraft: payload.isDraft,
            publishAt: payload.publishAt ?? '',
            access: payload.access,
            reportType: payload.reportType,
            routeNames: payload.pageLinks.map(pageLink => pageLink.routeName),
            siteAreaRelevance: payload.siteAreaRelevance,
            richTextImages: payload.richTextImages,
        };

        if (payload.thumbnailFile) {
            await deltaGptApi.uploadSparkContentWithImage(updatedDocument, payload.thumbnailFile);
        } else {
            await deltaGptApi.addOrUpdateSparkContentAdmin(updatedDocument);
        }

        showRichContentEditor.value = false;
        richContentEditorThread.value = null;
        richContentEditorDocument.value = null;

        await deltaGptStore.fetchAllDocumentInfo();
        emit('updated');
        toast.add({
            severity: 'success',
            summary: 'Saved',
            detail: 'Community thread saved.',
            life: 3000,
        });
    } catch {
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Could not save this Community thread.',
            life: 5000,
        });
    } finally {
        isSavingRichContent.value = false;
    }
}

function mapRichContentToEditorThread (document: IAdminSparkContent): ICommunityThreadDetails {
    return {
        threadId: document.documentShortCode,
        title: document.documentTitle,
        description: document.description ?? '',
        reportType: isThreadReportType(document.reportType) ? document.reportType : 'Post',
        bodyHtml: document.bodyHtml,
        linkedinUrl: document.linkedinUrl ?? null,
        linkedinEmbedUrl: document.linkedinEmbedUrl ?? null,
        pageLinks: document.routeNames.map(routeName => ({ routeName })),
        thumbnailUrl: document.thumbnailUrl,
        fullImageUrl: document.fullImageUrl,
        publicationDate: document.publicationDate,
        created: document.created,
        updatedAt: document.updatedAt,
        latestActivityAt: document.updatedAt,
        commentCount: document.commentCount,
        authorName: document.authors[0] ?? null,
        authors: document.authors,
        access: document.access,
        siteAreaRelevance: document.siteAreaRelevance,
        canModify: true,
        comments: [],
    };
}

async function initialise (): Promise<void> {
    isInitialising.value = true;

    try {
        if (isThreadContent.value || props.previewMode) {
            ourExperts.value = [];
            return;
        }

        ourExperts.value = await deltaGptApi.getExperts({ subscriptions: [] });
    } finally {
        isInitialising.value = false;
    }
}

watch(() => props.sparkContentDetails.documentShortCode, () => {
    initialise();
}, { immediate: true });
</script>

<style lang="scss" scoped>
@use '/src/assets/scss/components/research/documentView.scss';

.brand-loader-wrapper {
    position: relative;
    height: 100%;
    min-height: 0;
}

.edit-loader {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
}

.document-viewer.linkedin-embed-mode {
    &.modal-mode,
    &.preview-mode {
        height: 100%;
        min-height: 0;
        grid-template-rows: minmax(0, 1fr);

        .main-content {
            display: flex;
            flex: 1 1 auto;
            height: 100%;
            min-height: 0;
        }
    }

    &:not(.modal-mode):not(.preview-mode) {
        .main-content {
            display: block;
            height: auto;
        }

        .spark-content-card.full-height,
        .report-section.full-height,
        .spark-content-body {
            height: auto;
            min-height: 0;
        }

        .report-section.full-height {
            flex: 0 0 auto;
        }

        .linkedin-embed-wrapper {
            flex: 0 0 auto;
            min-height: clamp(50rem, 70vh, 90rem);
        }
    }
}

.document-viewer.modal-mode {
    grid-template-columns: 1fr;
    grid-template-rows: minmax(0, 1fr);
    grid-template-areas: "main";
    height: 100%;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;

    .side-content {
        display: none;
    }

    .main-content {
        width: 100%;
        padding: 0;
    }

}

.document-viewer.preview-mode {
    grid-template-columns: 1fr;
    grid-template-rows: minmax(0, 1fr);
    grid-template-areas: "main";
    height: 100%;
    min-height: 0;
    overflow: auto;

    .main-content {
        width: 100%;
        padding: 0;
    }

}

.document-viewer:not(.modal-mode):not(.preview-mode) {
    height: 100%;

    .spark-content-card:not(.full-height) {
        overflow: visible;
    }

    .report-section:not(.full-height) {
        overflow-y: visible !important;
    }

    .spark-content-body {
        height: auto;
    }
}

.side-content {
    .transpose-options {
        display: flex;
        justify-content: space-between;
        gap: var(--defaultSpacing);

        .grow {
            flex: 1;
            display: flex;
        }
    }
}

.report-section {
    overflow-y: hidden !important;
    padding: var(--largeSpacing);
    height: auto;

    &.no-access {
        padding-top: 10rem;
    }

    &.full-height {
        display: flex;
        flex: 1 1 0;
        flex-direction: column;
        height: 100%;
        min-height: 0;
    }
}

.spark-content-card {
    display: flex;
    flex-direction: column;
    overflow: hidden;

    &.full-height {
        flex: 1 1 auto;
        height: 100%;
        min-height: 0;
        width: 100%;
    }
}

.document-viewer.modal-mode,
.document-viewer.preview-mode {
    .spark-content-card:not(.full-height) {
        display: contents;
    }
}

.spark-content-report-details {
    flex: 0 0 auto;
}

.spark-content-body {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: var(--largeSpacing);
    height: 100%;
    min-height: 0;
}

.linkedin-link {
    .linkedin-icon {
        color: var(--actionColour);
    }
}

.mobile-linkedin-link {
    align-self: flex-start;
    margin-top: var(--smallSpacing);
}

.desktop-linkedin-link {
    display: inline-flex;
}

@media (min-width: 769px) {
    .mobile-linkedin-link {
        display: none;
    }
}

@media (max-width: 768px) {
    .desktop-linkedin-link {
        display: none;
    }
}

.linkedin-embed-wrapper {
    display: flex;
    flex: 1 1 0;
    justify-content: center;
    min-height: 0;
    width: 100%;

    iframe {
        display: block;
        flex: 1 1 auto;
        border: 1px solid var(--neutral300);
        border-radius: var(--borderRadiusOuter);
        background: var(--neutral0);
        height: 100%;
        width: 100%;
    }
}

.rich-content-header-extras {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: var(--defaultSpacing);
    padding: 0 var(--largeSpacing) var(--defaultSpacing);
}

.spark-content-modal-comments {
    border-top: 1px solid var(--neutral300);
    margin-top: var(--largeSpacing);
    padding-top: var(--largeSpacing);
}

.action-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: var(--defaultSpacing);
}
</style>
